// Milestone event contract (pacing spec §6 C1 — docs/11-features/pacing-breaks-and-cinematics.md).
//
// The sim emits a milestone as a presentation event through wasm
// `drainEvents()`, in the same envelope as every other sim event:
//
//   { id: 'sim_evt_N', type: 'milestone:reached', source?, target?, timestamp,
//     payload: MilestonePayload }
//
// Milestones are facts: emitting one never changes gameplay (unless the
// ruleset's `victory.milestones` module counts them). Client code — the
// cinematic director, chapter cards, the Explorer milestone tracker — reads
// them; it must never feed anything back into the sim.

/** Wire `type` of every milestone event. */
export const MILESTONE_EVENT_TYPE = 'milestone:reached' as const;

/**
 * Every milestone id in the contract. Additive only — never rename or
 * remove. All except `graduation.new_rule` are emitted by the sim; that
 * one is offered by the client itself (rulesets R8, the guided new-rule
 * cinematic) and never crosses the wire from the sim. Clients should
 * ignore unknown `data` keys and fall back gracefully on unknown values
 * (e.g. a new victory path).
 */
export const MILESTONE_IDS = [
  'city.first_founded', // a player's second city (the first is the capital)
  'city.tier_up', // a city grows a tier; data.tier = new tier (2 = town, 3 = city, 4 = metropolis, 5 = megacity)
  'city.first_capture', // the player's first capture of an enemy or neutral city
  'city.capital_captured', // an enemy capital falls to this player; data.fromPlayerIndex
  'hero.first_recruited',
  'hero.level_milestone', // data.level = 5 | 10
  'doctrine.tier_reached', // data.archetype, data.tier = 2 | 3
  'faith.temple_built', // first temple
  'wonder.built', // ids from the sim's config.milestones.wonder_building_ids; data.building
  'magic.ritual_completed', // data.ritual = ritual id
  'diplomacy.peace_signed', // data.otherPlayerIndex
  'battle.decisive', // >= 5 units killed on one side in one fight; 5 min cooldown per player; data.killed, data.otherPlayerIndex (the loser)
  'era.advanced', // data.era = 1.. (power-score thresholds)
  'victory.achieved',
  'graduation.new_rule', // client-offered (R8); the sim never emits it // data.path = conquest | economy | science | faith | diplomacy | team_power
  'legend.summoned', // a Champion finished its summon project (legends.enabled only); data.legend = kind, data.level
  'legend.fallen', // a Champion died; data.legend, data.deaths, data.respawnTick
  'sea.merchant_route', // a foreign house opened a trade route with one of your harbours (sea.trade); data.routes = how many are open
] as const;

export type MilestoneId = (typeof MILESTONE_IDS)[number];

/**
 * How big a moment it is. Drives presentation: `small` → banner/toast,
 * `big` → camera cinematic, `finale` → end-of-match sequence.
 */
export type MilestoneTier = 'small' | 'big' | 'finale';

export interface MilestonePayload {
  readonly milestoneId: MilestoneId;
  /** Player the milestone belongs to (0-based). */
  readonly playerIndex: number;
  readonly tier: MilestoneTier;
  /** Sim tick it happened on. */
  readonly tick: number;
  /** Entity the moment is about (city, hero, …), as the sim's u64 entity id. */
  readonly entityId?: number;
  /** World position [x, y, z] to frame (same encoding as faith:sanctified). */
  readonly position?: readonly [number, number, number];
  /** Display name for banners, e.g. the city or hero name. */
  readonly name?: string;
  /** Milestone-specific extras (see comments on MILESTONE_IDS). */
  readonly data?: Readonly<Record<string, string | number | boolean>>;
}

export function isMilestonePayload(p: unknown): p is MilestonePayload {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.milestoneId === 'string' &&
    (MILESTONE_IDS as readonly string[]).includes(o.milestoneId) &&
    typeof o.playerIndex === 'number' &&
    (o.tier === 'small' || o.tier === 'big' || o.tier === 'finale') &&
    typeof o.tick === 'number'
  );
}
